import { createTheme, Theme } from "@mui/material/styles";

const fontFamily = [
  "'Inter'",
  "-apple-system",
  "BlinkMacSystemFont",
  "'Segoe UI'",
  "Roboto",
  "sans-serif",
].join(",");

const basePalette = {
  primary: {
    main: "#0B1929",
    light: "#1A2B45",
    dark: "#060E18",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#00B4D8",
    light: "#48CAE4",
    dark: "#0096B7",
    contrastText: "#FFFFFF",
  },
  success: {
    main: "#10B981",
    light: "#34D399",
    dark: "#059669",
  },
  warning: {
    main: "#F59E0B",
    light: "#FBBF24",
    dark: "#D97706",
  },
  error: {
    main: "#EF4444",
    light: "#F87171",
    dark: "#DC2626",
  },
  info: {
    main: "#3B82F6",
    light: "#60A5FA",
    dark: "#2563EB",
  },
  background: {
    default: "#F8FAFC",
    paper: "#FFFFFF",
  },
  text: {
    primary: "#1E293B",
    secondary: "#64748B",
    disabled: "#94A3B8",
  },
  divider: "#E2E8F0",
};

function buildTheme(accentColor?: { main: string; light: string; dark: string }): Theme {
  const secondary = accentColor
    ? { ...accentColor, contrastText: "#FFFFFF" }
    : basePalette.secondary;

  return createTheme({
    palette: {
      ...basePalette,
      secondary,
    },
    typography: {
      fontFamily,
      h1: {
        fontSize: "2rem",
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: "-0.01em",
      },
      h2: {
        fontSize: "1.5rem",
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: "-0.005em",
      },
      h3: {
        fontSize: "1.25rem",
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: "1.125rem",
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: "1rem",
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: "0.875rem",
        fontWeight: 600,
        lineHeight: 1.5,
      },
      subtitle1: {
        fontSize: "1rem",
        fontWeight: 500,
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: "0.875rem",
        fontWeight: 500,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: "1rem",
        fontWeight: 400,
        lineHeight: 1.6,
      },
      body2: {
        fontSize: "0.875rem",
        fontWeight: 400,
        lineHeight: 1.6,
      },
      caption: {
        fontSize: "0.75rem",
        fontWeight: 400,
        lineHeight: 1.5,
        color: "#64748B",
      },
      button: {
        fontSize: "0.875rem",
        fontWeight: 600,
        textTransform: "none",
        letterSpacing: "0.01em",
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: "#F8FAFC",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          variant: "contained",
          color: "primary",
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
            padding: "8px 20px",
            "&:hover": {
              boxShadow: "0 4px 12px rgba(11, 25, 41, 0.15)",
            },
          },
          containedSecondary: {
            color: "#FFFFFF",
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)",
            border: "none",
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
          elevation1: {
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)",
          },
          elevation2: {
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.03)",
          },
          elevation3: {
            boxShadow: "0 10px 15px rgba(0, 0, 0, 0.05), 0 4px 6px rgba(0, 0, 0, 0.03)",
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: "outlined",
          size: "small",
        },
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 8,
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-head": {
              backgroundColor: "#0B1929",
              color: "#FFFFFF",
              fontWeight: 600,
              fontSize: "0.8125rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: "1px solid #E2E8F0",
            padding: "12px 16px",
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: "#0B1929",
            boxShadow: "none",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: "#0B1929",
            color: "#E2E8F0",
            borderRight: "none",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            fontWeight: 500,
            fontSize: "0.8125rem",
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: "#1E293B",
            fontSize: "0.75rem",
            borderRadius: 6,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
          },
        },
      },
    },
  });
}

const tenantAccents: Record<string, { main: string; light: string; dark: string }> = {
  bigfoot: {
    main: "#00B4D8",
    light: "#48CAE4",
    dark: "#0096B7",
  },
  spacex: {
    main: "#8B5CF6",
    light: "#A78BFA",
    dark: "#7C3AED",
  },
  industrial: {
    main: "#F59E0B",
    light: "#FBBF24",
    dark: "#D97706",
  },
};

export function getThemeByTenant(serverType: string): Theme {
  const accent = tenantAccents[serverType.toLowerCase()];
  return buildTheme(accent);
}

const theme = buildTheme();
export default theme;
